import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FaCalendarAlt, FaTimes } from "react-icons/fa";

/**
 * FilterBar
 *
 * Props:
 * ─────────────────────────────────────────────────────────────────────────────
 * dateFilterState      { start, end, showDateRangeDropdown }
 * onDateChange         (type: "start"|"end", value: string) => void
 * onClearDateFilter    () => void
 * showDatePicker       boolean  — show the date inputs (default: true)
 *
 * activeRange          string   — currently selected range key
 * onRangeChange        (key: string) => void
 * rangeOptions         [{ key, label }]  — pass only the options you want
 * rangeLabel           string   — label before the dropdown (optional)
 * showRangeDropdown    boolean  — show the range dropdown (default: true)
 *
 * filters              [{ type:"select", key, label, value, onChange, options:[{key,label}] }]
 *                      — any extra dropdowns you need
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * NOTE: the custom date popup is rendered with a portal (document.body) and
 * position: fixed, so parents with overflow-hidden / overflow-x-auto
 * (like DynamicTable) can never clip it.
 */

const POPUP_WIDTH = 280;
const POPUP_HEIGHT_ESTIMATE = 270;
const GAP = 8;

const FilterBar = ({
  // Date picker
  dateFilterState,
  onDateChange,
  onClearDateFilter,
  showDatePicker = true,

  // Range dropdown
  activeRange,
  onRangeChange,
  rangeOptions = [],
  rangeLabel = "",
  showRangeDropdown = true,

  // Extra dynamic filters
  filters = [],
  className = "",
}) => {
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  const hasDateFilter = dateFilterState?.start || dateFilterState?.end;

  // Hide the range dropdown when the user has typed custom dates
  // that don't match any predefined range
  const shouldShowRangeDropdown =
    showRangeDropdown &&
    rangeOptions.length > 0 &&
    dateFilterState?.showDateRangeDropdown !== false;

  // Show clear button only when there are custom dates AND the dropdown is hidden
  const shouldShowClearButton = hasDateFilter && !shouldShowRangeDropdown;

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Work out where the popup should sit (below the trigger, or above if no room)
  const updatePopupPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    let top = rect.bottom + GAP;
    if (
      top + POPUP_HEIGHT_ESTIMATE > window.innerHeight &&
      rect.top - GAP - POPUP_HEIGHT_ESTIMATE > 0
    ) {
      top = rect.top - GAP - POPUP_HEIGHT_ESTIMATE;
    }

    let left = rect.left;
    if (left + POPUP_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - POPUP_WIDTH - 8);
    }

    setPopupPos({ top, left });
  };

  // Position when opened, and keep it in sync on scroll / resize
  useLayoutEffect(() => {
    if (!showCustomDatePicker) return;
    updatePopupPosition();

    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true); // capture: catches inner scroll containers
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [showCustomDatePicker]);

  // Close picker when clicking outside (trigger AND popup both count as "inside")
  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideTrigger =
        triggerRef.current && triggerRef.current.contains(event.target);
      const insidePopup =
        popupRef.current && popupRef.current.contains(event.target);
      if (!insideTrigger && !insidePopup) {
        setShowCustomDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get display text for selected range
  const getDisplayText = () => {
    if (dateFilterState?.start && dateFilterState?.end) {
      return `${formatDisplayDate(dateFilterState.start)} - ${formatDisplayDate(dateFilterState.end)}`;
    }
    if (dateFilterState?.start) {
      return `From ${formatDisplayDate(dateFilterState.start)}`;
    }
    if (dateFilterState?.end) {
      return `Until ${formatDisplayDate(dateFilterState.end)}`;
    }
    return "Select date range";
  };

  const popup = showCustomDatePicker
    ? createPortal(
        <div
          ref={popupRef}
          style={{
            position: "fixed",
            top: popupPos.top,
            left: popupPos.left,
            width: POPUP_WIDTH,
            zIndex: 9999,
          }}
          className="bg-white border border-gray-200 rounded-lg shadow-lg p-4"
        >
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={dateFilterState?.start || ""}
                onChange={(e) => onDateChange("start", e.target.value)}
                className="text-sm px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-full"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                End Date
              </label>
              <input
                type="date"
                value={dateFilterState?.end || ""}
                onChange={(e) => onDateChange("end", e.target.value)}
                className="text-sm px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-full"
              />
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setShowCustomDatePicker(false)}
                className="flex-1 px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Apply
              </button>
              {(dateFilterState?.start || dateFilterState?.end) && (
                <button
                  onClick={() => {
                    onClearDateFilter();
                    setShowCustomDatePicker(false);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={className || "w-full"}>
      <div className="flex items-center gap-2 flex-nowrap">

        {/* ── Display Date Range with Calendar Icon ─────────────────────────── */}
        {showDatePicker && (
          <div className="relative shrink-0" ref={triggerRef}>
            <div
              className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-1.5 shadow-sm hover:border-gray-400 transition-all cursor-pointer h-9 whitespace-nowrap"
              onClick={() => setShowCustomDatePicker((prev) => !prev)}
            >
              <FaCalendarAlt className="w-3.5 h-3.5 text-gray-400" />
              <span
                className={`text-sm ${!hasDateFilter ? "text-gray-400" : "text-gray-700"}`}
              >
                {getDisplayText()}
              </span>
              {shouldShowClearButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClearDateFilter();
                  }}
                  className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear date filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Custom Date Picker popup (rendered in a portal) */}
            {popup}
          </div>
        )}

        {/* ── Range Dropdown ────────────────────────────────────────────────── */}
        {shouldShowRangeDropdown && (
          <div className="flex items-center gap-1 shrink-0">
            {rangeLabel && (
              <label className="text-sm text-gray-600 whitespace-nowrap">
                {rangeLabel}
              </label>
            )}
            <select
              value={activeRange || ""}
              onChange={(e) => onRangeChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9 shrink-0"
            >
              {rangeOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ── Dynamic Extra Filters ─────────────────────────────────────────── */}
        {filters.map((filter, index) => {
          if (filter.type === "select") {
            return (
              <div
                key={filter.key || index}
                className="flex items-center gap-1"
              >
                {filter.label && (
                  <label className="text-sm text-gray-600 whitespace-nowrap">
                    {filter.label}
                  </label>
                )}
                <select
                  value={filter.value || ""}
                  onChange={(e) => filter.onChange(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9"
                >
                  {filter.options.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          }
          return null;
        })}

      </div>
    </div>
  );
};

export default FilterBar;